import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Save, Loader2, CreditCard, CheckCircle, AlertTriangle, Database, Server, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALL_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "tikkie",        label: "Tikkie" },
  { value: "stripe_link",   label: "Stripe / Online" },
  { value: "cash",          label: "Cash" },
  { value: "other",         label: "Other" },
];

const BLANK = {
  beneficiary_name: "",
  iban: "",
  bic: "",
  bank_name: "",
  beneficiary_addr: "",
  bank_addr: "",
  tikkie_url: "",
  payment_note: "Please mention invoice number as payment reference.",
  methods: ["bank_transfer"],
  google_review_url: "",
  sources: {},
};

function SourceBadge({ src }) {
  if (src === "env")
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200 ml-1.5">
        <Server className="w-2.5 h-2.5" />ENV
      </span>
    );
  if (src === "db")
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-green-50 text-green-600 border border-green-200 ml-1.5">
        <Database className="w-2.5 h-2.5" />DB
      </span>
    );
  return null;
}

export default function PaymentSettings() {
  const { getAuthHeaders } = useAuth();
  const [form,    setForm]    = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/payment-settings`, { headers: getAuthHeaders() })
      .then(r => setForm({ ...BLANK, ...r.data }))
      .catch(() => toast.error("Failed to load settings"))
      .finally(() => setLoading(false));
  }, []);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/payment-settings`, form, { headers: getAuthHeaders() });
      toast.success("Payment settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const src = field => (form.sources || {})[field] || "not_set";

  return (
    <DashboardLayout>
      <div className="p-8 max-w-2xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">Payment Settings</h1>
            <p className="text-slate-600 mt-1">Default payment instructions used on invoices and emails</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-purple-700 hover:bg-purple-800 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900" />
          </div>
        ) : (
          <div className="space-y-6">

            {/* Source legend */}
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="font-medium">Value sources:</span>
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-green-50 text-green-600 border border-green-200 text-[10px] font-semibold">
                <Database className="w-2.5 h-2.5" />DB
              </span>
              <span className="text-slate-400">= saved in this page</span>
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200 text-[10px] font-semibold">
                <Server className="w-2.5 h-2.5" />ENV
              </span>
              <span className="text-slate-400">= from server environment variable</span>
            </div>

            {/* Status summary */}
            <div className="p-4 rounded-lg border space-y-2" style={{ background: "#f0f4ff", borderColor: "#bfcfff" }}>
              <p className="text-xs font-semibold" style={{ color: "#1e3a5f" }}>Runtime configuration</p>
              <div className="flex flex-wrap gap-3">
                {form.iban ? (
                  <span className="text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />IBAN: {form.iban}
                    <SourceBadge src={src("iban")} />
                  </span>
                ) : (
                  <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />Bank Transfer: IBAN not set
                  </span>
                )}
                {form.tikkie_api_configured ? (
                  <span className="text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />Tikkie API connected
                    <span className="text-[10px] font-semibold px-1 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200 ml-0.5">ENV</span>
                  </span>
                ) : (
                  <span className="text-[11px] text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">Tikkie API: not connected</span>
                )}
                {form.stripe_configured ? (
                  <span className="text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />Stripe connected
                    <span className="text-[10px] font-semibold px-1 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200 ml-0.5">ENV</span>
                  </span>
                ) : (
                  <span className="text-[11px] text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">Stripe: not connected</span>
                )}
              </div>
              <p className="text-[10px] text-slate-400">
                Values shown here are the <strong>effective runtime values</strong> used in all invoice PDFs, emails, and reminders.
                ENV values come from server environment variables; DB values are saved here and override ENV.
                Tikkie API and Stripe credentials are always ENV-only.
              </p>
            </div>

            {/* Default payment methods */}
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-purple-600" />
                  <h2 className="text-sm font-semibold text-slate-700">Default Payment Methods</h2>
                </div>
                <p className="text-xs text-slate-500">These will be pre-selected when creating new invoices. Can be overridden per invoice.</p>
                <div className="flex flex-wrap gap-4">
                  {ALL_METHODS.map(opt => (
                    <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm text-slate-700">
                      <Checkbox
                        checked={(form.methods || []).includes(opt.value)}
                        onCheckedChange={checked => {
                          setField("methods",
                            checked
                              ? [...(form.methods || []), opt.value]
                              : (form.methods || []).filter(m => m !== opt.value)
                          );
                        }}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Bank Transfer */}
            <Card>
              <CardContent className="p-5 space-y-4">
                <h2 className="text-sm font-semibold text-slate-700">Bank Transfer Details</h2>
                <p className="text-xs text-slate-500">
                  Shown on invoice PDFs and payment emails when "Bank Transfer" is selected.
                  Fields pre-populated from your environment — edit and save to store a DB override.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">
                      Beneficiary Name<SourceBadge src={src("beneficiary_name")} />
                    </Label>
                    <Input value={form.beneficiary_name}
                      onChange={e => setField("beneficiary_name", e.target.value)}
                      placeholder="The Better Fare"
                      className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      Bank Name<SourceBadge src={src("bank_name")} />
                    </Label>
                    <Input value={form.bank_name}
                      onChange={e => setField("bank_name", e.target.value)}
                      placeholder="ING, Revolut, ABN AMRO…"
                      className="mt-1 h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      IBAN<SourceBadge src={src("iban")} />
                    </Label>
                    <Input value={form.iban}
                      onChange={e => setField("iban", e.target.value)}
                      placeholder="NL00 BANK 0000 0000 00"
                      className="mt-1 h-9 text-sm font-mono" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">
                      BIC / SWIFT<SourceBadge src={src("bic")} />
                    </Label>
                    <Input value={form.bic}
                      onChange={e => setField("bic", e.target.value)}
                      placeholder="INGBNL2A"
                      className="mt-1 h-9 text-sm font-mono" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">
                      Beneficiary Address<SourceBadge src={src("beneficiary_addr")} />
                    </Label>
                    <Input value={form.beneficiary_addr}
                      onChange={e => setField("beneficiary_addr", e.target.value)}
                      placeholder="Street, City, Country"
                      className="mt-1 h-9 text-sm" />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">
                      Bank Address<SourceBadge src={src("bank_addr")} />
                    </Label>
                    <Input value={form.bank_addr}
                      onChange={e => setField("bank_addr", e.target.value)}
                      placeholder="Bank street address (optional)"
                      className="mt-1 h-9 text-sm" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tikkie */}
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-slate-700">Tikkie Default URL</h2>
                  <SourceBadge src={src("tikkie_url")} />
                </div>
                <p className="text-xs text-slate-500">
                  Optional global Tikkie link. Per-invoice links generated via the invoice page take priority.
                  {form.tikkie_api_configured
                    ? " Tikkie API is connected — per-invoice links can be auto-generated."
                    : " Tikkie API not connected — only static links work."}
                </p>
                <Input value={form.tikkie_url}
                  onChange={e => setField("tikkie_url", e.target.value)}
                  placeholder="https://tikkie.me/pay/…"
                  className="h-9 text-sm" />
              </CardContent>
            </Card>

            {/* Stripe info */}
            <Card>
              <CardContent className="p-5 space-y-2">
                <h2 className="text-sm font-semibold text-slate-700">Stripe</h2>
                {form.stripe_configured ? (
                  <p className="text-xs text-slate-500">
                    Stripe API is connected (via environment). To include a Stripe payment link on an invoice,
                    paste the specific payment URL in the invoice's Payment Instructions override.
                    Automatic Stripe link generation is not yet implemented.
                  </p>
                ) : (
                  <p className="text-xs text-slate-400">
                    Stripe API not connected. Set <code className="text-[11px] bg-slate-100 px-1 py-0.5 rounded">STRIPE_API_KEY</code> in server environment to enable.
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Payment note */}
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold text-slate-700">Standard Payment Note</h2>
                  <SourceBadge src={src("payment_note")} />
                </div>
                <p className="text-xs text-slate-500">Shown below bank details on invoices and payment emails.</p>
                <Input value={form.payment_note}
                  onChange={e => setField("payment_note", e.target.value)}
                  placeholder="Please mention invoice number as payment reference."
                  className="h-9 text-sm" />
              </CardContent>
            </Card>

            {/* Google Reviews */}
            <Card>
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" />
                  <h2 className="text-sm font-semibold text-slate-700">Google Reviews</h2>
                  {form.google_review_url
                    ? <span className="text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" />Configured</span>
                    : <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Not configured</span>
                  }
                </div>
                <p className="text-xs text-slate-500">
                  Used in review request emails sent from bookings, invoices, and visa quotes.
                  Find your link in <strong>Google Business Profile → Share Review Form</strong>.
                </p>
                <Input value={form.google_review_url}
                  onChange={e => setField("google_review_url", e.target.value)}
                  placeholder="https://g.page/r/…/review"
                  className="h-9 text-sm" />
              </CardContent>
            </Card>

          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
